import { Dispatch, useRef } from 'react'
import { useHistory } from 'react-router-dom'
import omit from 'lodash/omit'

import { getUserId as getMsUserId } from '../../../../services/api/microsoft'
import { getUserId as getGoogleUserId } from '../../../../services/api/google'
import { getUserId as getSlackUserId } from '../../../../services/api/slack'
import { PlatformEnum } from '@vacationtracker/shared/data/platforms'
import { ICheckUserId } from '@vacationtracker/shared/types/company'
import { vtUserId } from '@vacationtracker/shared/functions/user-id'
import { ITokens, IMicrosoftUser, TenantId } from '../../../../types/microsoft'
import { FrontendUrls } from '../../../../types/urls'

import { googleAuth, msAuth, slackAuth } from '../auth-clients'
import { checkUserIdToOutcome } from '../outcomes'
import { setSignupEmail, trackSignup, trackSignupStarted } from '../analytics'
import { PLATFORM_BRAND } from '../platform-brand'
import { ConnectPlatform, NewSignupAction } from '../types'
import { ISignupNavigation } from './use-signup-navigation'
import { IPlatformTokenSigninParams, ISignupSession } from './use-signup-session'
import { usePlatformErrorHandler } from './use-platform-error-handler'

/** `getBotToken` throws when the workspace has no stored token; absent is not an error here. */
const readBotToken = (): string | undefined => {
  try {
    return slackAuth.getBotToken()
  } catch (error) {
    return undefined
  }
}

const PLATFORM_CLICK_EVENT: Record<ConnectPlatform, string> = {
  [PlatformEnum.Slack]: 'SIGNUP_WITH_SLACK_BUTTON_CLICKED',
  [PlatformEnum.Microsoft]: 'SIGNUP_WITH_MICROSOFT_BUTTON_CLICKED',
  [PlatformEnum.Google]: 'SIGNUP_WITH_GOOGLE_BUTTON_CLICKED',
}

interface IResolveParams {
  userData: ICheckUserId
  platform: ConnectPlatform
  extra: Record<string, unknown>
  /** Slack needs its bot installed before company details, so it says where to go instead. */
  onNewCompany?: () => void
}

export interface IUsePlatformSignupParams {
  localDispatch: Dispatch<NewSignupAction>
  navigation: ISignupNavigation
  session: ISignupSession
}

export interface IPlatformSignup {
  /** Microsoft and Google are asked for the account `loginHint` names, when there is one. */
  signupWith: (platform: ConnectPlatform, loginHint?: string) => void
  /** The token the last sign-up with this platform answered with, for an ask to be added. */
  readProviderToken: (platform: ConnectPlatform) => string | undefined
}

export const usePlatformSignup = ({ localDispatch, navigation, session }: IUsePlatformSignupParams): IPlatformSignup => {
  const history = useHistory()
  // Neither is rendered, so neither belongs in state. The token is the proof an ask to be added
  // is sent with; the hint keeps Microsoft's consent retry on the account the visitor typed.
  const providerTokenRef = useRef<{ platform: ConnectPlatform; token: string } | null>(null)
  const microsoftLoginHintRef = useRef<string | undefined>(undefined)
  const { handlePlatformError } = usePlatformErrorHandler({
    onRetryMicrosoft: () => connectWithMicrosoft(true),
    onCancelled: (platform) => localDispatch({ type: 'signupCancelled', platform }),
  })

  /**
   * Every failure ends here, and releasing the form is the half that must never be forgotten:
   * without it the page keeps spinning even though the attempt is over.
   */
  const failPlatformSignup = (error, platform: string) => {
    localDispatch({ type: 'released' })
    handlePlatformError(error, platform)
  }

  const startPlatformSignup = (platform: ConnectPlatform) => {
    localDispatch({ type: 'submitted', source: platform })
    trackSignup(PLATFORM_CLICK_EVENT[platform], { platform })
    trackSignupStarted(platform)
  }

  /**
   * The one place a platform lookup is turned into an outcome. Every platform comes through
   * here, so the analytics, the banner and the busy flag cannot be forgotten on one of them.
   */
  const resolveUserData = ({ userData, platform, extra, onNewCompany }: IResolveParams) => {
    setSignupEmail(userData.mail)
    const outcome = checkUserIdToOutcome(userData)
    localDispatch({ type: 'platformResolved', outcome })
    trackSignup('SIGNUP_PLATFORM_RESULT', { platform, outcome: outcome.kind })

    if (outcome.kind === 'subscriptionExpired') return

    // Signing up cannot fix a deactivated account, and the page that can ask for it back is
    // the sign-in page, which owns the request and everything that follows it.
    if (outcome.kind === 'accountInactive') {
      trackSignup('SIGNUP_INACTIVE_ACCOUNT_SENT_TO_SIGNIN', { platform })
      navigation.goToLoginWithAccount({ email: outcome.email, inactive: true })
      return
    }

    // Signing up again cannot add anyone to a company that already exists, so the banner offers
    // the ask instead, sent with the sign-in this answer came from.
    if (outcome.kind === 'companyExists') {
      localDispatch({ type: 'companyExistsDetected', platform, organizationName: userData.organizationName || '', email: userData.mail || '' })
      trackSignup('ORG_ALREADY_EXISTS_PAGE_VIEWED', {
        ...omit(userData || {}, ['adminContacts', 'slackUser']),
        platform,
      })
      return
    }

    // Answered on this page for Slack too: sending it on to the install page meant adding the
    // bot to a workspace only to be told to sign in somewhere else.
    if (outcome.kind === 'newCompanyOnOtherPlatform') {
      navigation.saveCreateUser({ userData, platform, extra })
      const orgOnOtherPlatformPayload = { platform, existingOrganizationName: outcome.organizationName }
      trackSignup('ORG_EXISTS_ON_ANOTHER_PLATFORM_OPEN_MODAL', orgOnOtherPlatformPayload)
      trackSignup('SIGNUP_ORG_EXISTS_ON_ANOTHER_PLATFORM_SHOWN', orgOnOtherPlatformPayload)
      return
    }

    // Reached only once the token sign-in was tried on the active account the lookup found, and
    // refused. Company details cannot fix that: with no new company to set up, it bounced the
    // visitor straight back here with nothing said.
    if (outcome.kind === 'proceed') {
      trackSignup('SIGNUP_PLATFORM_SIGNIN_REJECTED', { platform })
      localDispatch({ type: 'platformSigninRejected', platform: PLATFORM_BRAND[platform].full })
      return
    }

    if (outcome.kind === 'newCompany') {
      navigation.saveCreateUser({ userData, platform, extra })
    }
    // Only on the way to the next step, matching where the old page wrote it: an address that
    // stopped at a modal is not one the visitor chose to carry forward.
    if (userData.mail) {
      sessionStorage.setItem('vtUserEmail', userData.mail)
    }
    if (onNewCompany) {
      onNewCompany()
      return
    }
    navigation.goToCompanyDetails()
  }

  /**
   * An account only exists to sign in to when the lookup found one. Attempting it regardless
   * costs a Cognito round-trip that is guaranteed to throw on the commonest path of all, a
   * brand-new company.
   */
  const signinIfAccountExists = async (params: IPlatformTokenSigninParams & { userData: ICheckUserId }) => {
    const { userData, ...signinParams } = params
    if (checkUserIdToOutcome(userData).kind !== 'proceed') {
      return false
    }
    try {
      return await session.signinWithPlatformToken(signinParams)
    } catch (error) {
      // Cognito has no account for this identity, which is ordinary for a new company.
      return false
    }
  }

  const connectWithSlack = () => {
    startPlatformSignup(PlatformEnum.Slack)

    slackAuth
      .connect(null, undefined, {
        // Signed out of Slack, its workspace sign-in cuts the tab off from this page.
        onTabOutOfSight: () => localDispatch({ type: 'platformTabOutOfSight', platform: PLATFORM_BRAND[PlatformEnum.Slack].full }),
      })
      .then(async (result) => {
        // An answer from a cut-off tab finds the form already freed, so it is taken back.
        localDispatch({ type: 'submitted', source: PlatformEnum.Slack })
        const slackUserId = result.authed_user.id
        const slackUserToken = result.authed_user.access_token
        providerTokenRef.current = { platform: PlatformEnum.Slack, token: slackUserToken }

        // Resolve the account first, the way Microsoft and Google do. Slack used to assume a
        // new company every time, so someone who already had an account was sent to install
        // the bot again just to be logged in at the end of it. This answer also carries the
        // name and address, so there is no second round-trip to Slack for them.
        const userData: ICheckUserId = await getSlackUserId(slackUserId, slackUserToken)

        // Use the token the consent screen just produced rather than asking for another one.
        const signedIn = await signinIfAccountExists({
          userData,
          username: userData.vacationTrackerId as string,
          token: slackUserToken,
          platform: PlatformEnum.Slack,
          email: userData.mail || '',
          botToken: readBotToken(),
        })
        if (signedIn) return

        if (checkUserIdToOutcome(userData).kind === 'proceed') {
          // The account exists but its light OpenID token could not satisfy Cognito. The sign-in
          // page owns the scope validation and full-scope retry, so it finishes the job rather
          // than this flow growing a second copy. Reported so the hand-off does not read as
          // unexplained drop-off between clicking and signing in.
          trackSignup('SIGNUP_EXISTING_ACCOUNT_SENT_TO_SIGNIN', { platform: PlatformEnum.Slack })
          history.push(`${FrontendUrls.signin}?platform=${PlatformEnum.Slack}`)
          return
        }

        resolveUserData({
          userData,
          platform: PlatformEnum.Slack,
          extra: {},
          // A company that does not exist yet still needs the bot installed first.
          onNewCompany: () => history.push(`${FrontendUrls.installSlackBot}/${result.team.id}?realName=${encodeURIComponent(userData.name || '')}`),
        })
      })
      .catch((error) => failPlatformSignup(error, PlatformEnum.Slack))
  }

  const connectWithMicrosoft = (interactionRequired = false) => {
    startPlatformSignup(PlatformEnum.Microsoft)

    // Promises, not async/await: window.open requires a synchronous call chain.
    msAuth
      .signin(['user.readbasic.all', 'team.readbasic.all'], interactionRequired, microsoftLoginHintRef.current)
      .then(async ([tokens, msUser, tenantId]: [ITokens, IMicrosoftUser, TenantId]) => {
        providerTokenRef.current = { platform: PlatformEnum.Microsoft, token: tokens.accessToken }
        // One lookup, not two. The old page called this once to resolve an id and again
        // after the sign-in attempt failed.
        const userData: ICheckUserId = await getMsUserId(tenantId, tokens.accessToken)
        setSignupEmail(userData.mail)
        msAuth.setTokens(tokens)

        const signedIn = await signinIfAccountExists({
          userData,
          username: vtUserId(userData.id || msUser.id, PlatformEnum.Microsoft),
          token: tokens.accessToken,
          platform: PlatformEnum.Microsoft,
          email: userData.mail || '',
        })
        if (signedIn) return

        resolveUserData({
          userData,
          platform: PlatformEnum.Microsoft,
          extra: { tenantId, msUserId: userData.id },
        })
      })
      .catch((error) => failPlatformSignup(error, PlatformEnum.Microsoft))
  }

  const connectWithGoogle = (loginHint?: string) => {
    startPlatformSignup(PlatformEnum.Google)

    googleAuth
      .signin(false, loginHint)
      .then(async () => {
        const googleUser = googleAuth.getSignedInUser()
        if (!googleUser.hd) {
          localDispatch({ type: 'notWorkspaceAccountRejected' })
          trackSignup('SIGNUP_WITH_GOOGLE_ERROR', {
            platform: PlatformEnum.Google,
            outcome: 'notWorkspaceAccount',
          })
          return
        }

        providerTokenRef.current = { platform: PlatformEnum.Google, token: googleAuth.getIdToken() }
        const userData: ICheckUserId = await getGoogleUserId(googleAuth.getIdToken())
        setSignupEmail(userData.mail)

        const signedIn = await signinIfAccountExists({
          userData,
          username: vtUserId(googleUser.sub, PlatformEnum.Google),
          token: googleAuth.getAccessToken(),
          platform: PlatformEnum.Google,
          email: userData.mail || '',
        })
        if (signedIn) return

        resolveUserData({
          userData,
          platform: PlatformEnum.Google,
          extra: {
            googleDomain: googleUser.hd,
            imageUrl: googleUser.picture,
            googleUserId: googleUser.sub,
          },
        })
      })
      .catch((error) => failPlatformSignup(error, PlatformEnum.Google))
  }

  const signupWith = (platform: ConnectPlatform, loginHint?: string) => {
    if (platform === PlatformEnum.Slack) return connectWithSlack()
    if (platform === PlatformEnum.Microsoft) {
      microsoftLoginHintRef.current = loginHint || undefined
      return connectWithMicrosoft()
    }
    return connectWithGoogle(loginHint || undefined)
  }

  const readProviderToken = (platform: ConnectPlatform): string | undefined => (providerTokenRef.current?.platform === platform ? providerTokenRef.current.token : undefined)

  return { signupWith, readProviderToken }
}
